// ── Mapping server / client results to what the editor shows ─────────────
// Pure; the server response (POST /expr-preview) is authoritative.

export const MAX_EXPRESSION_CHARS = 4000;   // server: code_expr/analyze.py MAX_EXPRESSION_CHARS

export function lengthStatus(src) {
  const count = String(src ?? '').length;
  return { count, limit: MAX_EXPRESSION_CHARS, over: count > MAX_EXPRESSION_CHARS, near: count > MAX_EXPRESSION_CHARS * 0.9 };
}

const ISSUE_TITLES = {
  EXPR_SYNTAX: 'Syntax error',
  EXPR_NOT_EXPRESSION: 'Not a single expression',
  EXPR_DYNAMIC_ACCESS: 'Column name must be fixed',
  EXPR_TOO_LONG: 'Expression is too long',
  EXPR_FORBIDDEN: 'Not allowed in code mode',
  EXPR_MISSING_RETURN: 'The code never returns a value',
};

// One validation line for a server issue {code, message, line, col}.
export function describeIssue(issue) {
  if (!issue) return null;
  const where = issue.line != null ? `line ${issue.line}, column ${issue.col ?? 1}` : null;
  let message = issue.message || 'The expression is not valid.';
  if (issue.code === 'EXPR_TOO_LONG') {
    message = `${message} Shorten it to ${MAX_EXPRESSION_CHARS} characters or fewer — nothing is cut off for you.`;
  } else if (issue.code === 'EXPR_DYNAMIC_ACCESS') {
    message = `${message} Use row.name or row["name"].`;
  }
  return { code: issue.code, title: ISSUE_TITLES[issue.code] || 'Problem', message, where };
}

const FAILURE_TITLES = {
  EXPR_TIMEOUT: 'The preview took too long',
  EXPR_MEMORY: 'The preview ran out of memory',
  EXPR_ENGINE: 'The expression engine failed',
};

export function describeFailure(failure) {
  if (!failure) return null;
  return { code: failure.code, title: FAILURE_TITLES[failure.code] || 'The preview failed', message: failure.message };
}

// "Valid, returns true/false per row" / a problem / nothing yet.
// `state` is one of: 'empty' | 'checking' | 'valid' | 'invalid' | 'failed' | 'offline'.
export function validationState({ src, response, error, pending }) {
  if (!String(src ?? '').trim()) return { state: 'empty' };
  if (lengthStatus(src).over && !(response && response.issues?.length)) {
    return { state: 'invalid', issue: describeIssue({ code: 'EXPR_TOO_LONG', message: `The expression is ${lengthStatus(src).count} characters long; the limit is ${MAX_EXPRESSION_CHARS}.` }) };
  }
  if (error) return { state: 'offline', message: error };
  if (!response) return { state: pending ? 'checking' : 'empty' };
  if (response.issues?.length) return { state: 'invalid', issue: describeIssue(response.issues[0]) };
  if (response.failure) return { state: 'failed', failure: describeFailure(response.failure) };
  return { state: 'valid' };
}

// The text under the live preview.
export function previewSummary(r) {
  if (!r || r.flags == null) return null;
  return {
    headline: `Matched ${r.matched} of ${r.total} rows`,
    truncatedNote: r.truncated ? `Showing the first ${r.rowCap ?? r.total} rows` : null,
    rowErrors: r.rowErrors?.count
      ? { count: r.rowErrors.count, examples: r.rowErrors.examples || [] } : null,
    nonBoolean: r.nonBoolean?.count
      ? { count: r.nonBoolean.count, examples: r.nonBoolean.examples || [] } : null,
  };
}
